import React, { useState, useRef, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { signOut } from 'aws-amplify/auth';
import { HelpCircle, LogOut, DollarSign, Search, Bell } from 'lucide-react';
import V2ThemeToggle from '../../../../app/src/shared/custom-ui/V2ThemeToggle';
import { useV2Theme } from '../../../../app/src/providers/theme/ThemeProvider';
import { ReactComponent as DarkThemeLogo } from '../../assets/svgs/symbian-health.svg';
import { ReactComponent as LightThemeLogo } from '../../assets/svgs/symbian-logo.svg';
import NavBar from '../NavBar';
import Icon from '../Icon';
import HeaderPatientInfo from '../HeaderPatientInfo';
import { PatientInfoVisibility } from '../../types/PatientInfoVisibility';
import VoiceRecording from '../../../../app/src/routes/voice-dictation/VoiceRecording';

const VoiceDictation = VoiceRecording as any;

const Icons = { HelpCircle, LogOut, DollarSign, Search, Bell } as Record<string, any>;

export interface HeaderProps {
    /** Primary toolbar content placed in the center */
    children?: ReactNode;
    /** Whether the back/return button is enabled */
    isReturnEnabled?: boolean;
    /** Callback when the return button is clicked */
    onClickReturnButton?: () => void;
    /** Sticky behavior */
    isSticky?: boolean;
    /** Control patient info visibility */
    showPatientInfo?: string;
    /** Services manager for HeaderPatientInfo */
    servicesManager?: any;
    /** Secondary toolbar slot (shown in breadcrumb area) */
    Secondary?: ReactNode;
    /** App config for HeaderPatientInfo */
    appConfig?: any;
    /** Sidebar menu toggle function */
    sidebarMenu?: null | (() => ReactNode);
    /** Show voice recording widget */
    showVoiceRecording?: boolean;
    /** Breadcrumb / secondary info */
    Breadcrumb?: ReactNode;
    /** Density: 'default' or 'compact' */
    density?: 'default' | 'compact';
    /** Show theme toggle (default true) */
    showThemeToggle?: boolean;
    showLogo?: boolean;
}

/* ─── Default Symbian Health Logo (theme-aware SVG) ────────── */
const SymbianLogo: React.FC<{ onClick?: () => void }> = ({ onClick }) => {
    const { isDark } = useV2Theme();
    const LogoSvg = isDark ? DarkThemeLogo : LightThemeLogo;

    return (
        <button
            type="button"
            onClick={onClick}
            aria-label="Go to homepage"
            className="flex items-center gap-3 group bg-transparent border-0 cursor-pointer p-0"
        >
            <LogoSvg
                className="h-10 w-auto mr-1 opacity-100 transition-opacity duration-200"
                aria-label="Symbian Health"
                preserveAspectRatio="xMidYMid meet"
            />
        </button>
    );
};

/* ─── Profile Avatar ─────────────────────────────────────────── */
const ProfileAvatar: React.FC = () => (
    <div className="h-[34px] w-[34px] rounded-[var(--v2-radius-md)] bg-[var(--v2-primary-600)] flex items-center justify-center text-white text-[13px] font-bold">
        <svg viewBox="0 0 24 24" className="h-[17px] w-[17px]" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0" />
        </svg>
    </div>
);

function Header({
    children,
    isReturnEnabled = false,
    onClickReturnButton,
    isSticky = false,
    showPatientInfo = PatientInfoVisibility.VISIBLE_COLLAPSED,
    servicesManager,
    Secondary,
    appConfig,
    sidebarMenu = null,
    showVoiceRecording = false,
    Breadcrumb,
    density = 'default',
    showThemeToggle = true,
    showLogo = true,
    ...props
}: HeaderProps) {
    const navigate = useNavigate();
    const voiceRecordingRef = useRef(null);
    const [profileOpen, setProfileOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    // Close profile dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setProfileOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const onClickReturn = () => {
        if (isReturnEnabled && onClickReturnButton) onClickReturnButton();
    };

    const handleLogout = async () => {
        setProfileOpen(false);
        try {
            await signOut();
            navigate('/');
        } catch (error) {
            console.log('error signing out: ', error);
        }
    };

    // density-based sizing (dynamic — must stay in style)
    const headerHeight = density === 'compact' ? 60 : 72;

    // Shared menu item className (replaces menuItemStyle object)
    const menuItemCls =
        'flex w-full items-center gap-3 px-[18px] py-[11px] text-[14.5px] font-medium border-0 bg-transparent text-[var(--v2-text-secondary)] cursor-pointer transition-all duration-[var(--v2-duration-fast)] ease-[var(--v2-ease-calm)] text-left hover:bg-[var(--v2-bg-hover)] hover:text-[var(--v2-text-primary)]';

    return (
        <NavBar className="" isSticky={isSticky} {...props}>
            <header
                className="relative flex items-center justify-between bg-[var(--v2-bg-card)] border-b-[1.5px] border-[var(--v2-border-card)] shadow-[var(--v2-shadow-header)]"
                style={{ height: headerHeight }}
                role="banner"
            >
                {/* ─── Left ────────────────────────────────────────── */}
                <div className="flex items-center gap-2 min-w-0">
                    {/* Sidebar toggle */}
                    {sidebarMenu && <div className="mr-[10px]">{sidebarMenu()}</div>}

                    {/* Back button */}
                    <div
                        className={`inline-flex items-center mr-1 ${isReturnEnabled ? 'cursor-pointer' : ''}`}
                        onClick={onClickReturn}
                        aria-hidden={!isReturnEnabled}
                    >
                        {isReturnEnabled && (
                            <button
                                type="button"
                                className="p-[10px] rounded-[var(--v2-radius-md)] border-0 bg-transparent cursor-pointer text-[var(--v2-text-secondary)] transition-all duration-200 hover:bg-[var(--v2-bg-hover)]"
                                aria-label="Go back"
                            >
                                <Icon name="chevron-left" className="w-5 h-5" />
                            </button>
                        )}
                    </div>

                    {/* Brand logo */}
                    {showLogo && <SymbianLogo onClick={() => navigate('/')} />}

                    {/* Breadcrumb / Secondary toolbar */}
                    <div className="hidden sm:block text-[#566d82] dark:text-[#7a9ab5] ml-4 text-[14px] font-medium">
                        {Breadcrumb ?? Secondary}
                    </div>
                </div>

                {/* ─── Center (Primary Toolbar via children) ────── */}
                <div className="pointer-events-none absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 sm:block">
                    <div className="pointer-events-auto flex items-center justify-center gap-2">
                        {children}
                    </div>
                </div>

                {/* ─── Right ───────────────────────────────────────── */}
                <div className="flex items-center gap-2 ml-auto shrink-0 pr-6">
                    {/* Patient Info */}
                    {showPatientInfo !== PatientInfoVisibility.DISABLED && servicesManager && appConfig && (
                        <div className="hidden select-none pl-1 sm:block">
                            <HeaderPatientInfo servicesManager={servicesManager} appConfig={appConfig} />
                        </div>
                    )}

                    {/* Right-side pill: theme toggle + voice dictation + profile */}
                    <div className="flex items-center gap-1 px-[6px] py-[4px] rounded-[var(--v2-radius-lg)] border border-[var(--v2-border-subtle)] bg-[var(--v2-bg-card)]">
                        {/* Theme Toggle */}
                        {showThemeToggle && <V2ThemeToggle mode="simple" compact />}

                        {/* Divider after theme toggle */}
                        {showThemeToggle && (
                            <div className="w-px h-5 bg-[var(--v2-border-subtle)] mx-[2px] shrink-0" />
                        )}

                        {/* Voice Dictation — left of profile button */}
                        {showVoiceRecording && (
                            <>
                                <VoiceDictation ref={voiceRecordingRef} variant="header" />
                                <div className="w-px h-5 bg-[var(--v2-border-subtle)] mx-[2px] shrink-0" />
                            </>
                        )}

                        {/* Profile dropdown */}
                        <div ref={dropdownRef} className="relative">
                            <button
                                type="button"
                                onClick={() => setProfileOpen((v) => !v)}
                                aria-haspopup="menu"
                                aria-expanded={profileOpen}
                                aria-label="Open menu"
                                className="p-1 rounded-[var(--v2-radius-md)] border-0 bg-transparent cursor-pointer transition-all duration-[var(--v2-duration-fast)] hover:bg-[var(--v2-bg-hover)]"
                            >
                                <ProfileAvatar />
                            </button>

                            {profileOpen && (
                                <div
                                    role="menu"
                                    className="absolute right-0 top-[calc(100%+10px)] z-[9999] w-56 rounded-[var(--v2-radius-xl)] border border-[var(--v2-border-glass-strong)] bg-[var(--v2-glass-dropdown)] overflow-hidden"
                                    style={{
                                        backdropFilter: 'blur(16px)',
                                        WebkitBackdropFilter: 'blur(16px)',
                                        boxShadow: 'var(--v2-shadow-premium)',
                                        animation: 'v2-fade-in-up var(--v2-duration-fast) var(--v2-ease-enter)',
                                    }}
                                >
                                    <div className="py-2">
                                        {/* Billing */}
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={() => { setProfileOpen(false); navigate('/billing'); }}
                                            className={menuItemCls}
                                        >
                                            <Icons.DollarSign className="h-4 w-4 text-[var(--v2-primary-400)]" />
                                            Billing
                                        </button>

                                        {/* Divider */}
                                        <div className="mx-4 my-1 h-px bg-[var(--v2-border-subtle)]" />

                                        {/* Help */}
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={() => { setProfileOpen(false); navigate('/support'); }}
                                            className={menuItemCls}
                                        >
                                            <Icons.HelpCircle className="h-4 w-4 text-[var(--v2-primary-400)]" />
                                            Help & Support
                                        </button>

                                        {/* Divider */}
                                        <div className="mx-4 my-1 h-px bg-[var(--v2-border-subtle)]" />

                                        {/* Sign out */}
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={handleLogout}
                                            className="flex w-full items-center gap-3 px-[18px] py-[11px] text-[14.5px] font-medium border-0 bg-transparent text-[var(--v2-danger-500)] cursor-pointer transition-all duration-[var(--v2-duration-fast)] ease-[var(--v2-ease-calm)] text-left hover:bg-[var(--v2-danger-50)]"
                                        >
                                            <Icons.LogOut className="h-4 w-4" />
                                            Sign out
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* end pill */}
                    </div>
                </div>
            </header>
        </NavBar>
    );
}

Header.propTypes = {
    sidebarMenu: PropTypes.oneOfType([PropTypes.node, PropTypes.any]),
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    isReturnEnabled: PropTypes.bool,
    isSticky: PropTypes.bool,
    onClickReturnButton: PropTypes.func,
    showPatientInfo: PropTypes.string,
    servicesManager: PropTypes.object,
    showVoiceRecording: PropTypes.bool,
};

export default Header;
